import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Slider from 'rc-slider';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { moneyFormatter, percentFormatter } from 'modules/core/utils';
import { ColumnWidth } from 'modules/core/enums';
import { shallowEqual, useSelector } from 'react-redux';

import { ProbabilityEstimatorCalculationService } from '../services';
import { DEFAULT_MAXIMAL_PAYMENT_RANGE, MINIMAL_PAYMENT_RANGE } from '../probabilityEstimator.const';
import { CreditScoreColors } from '../enums/creditScoreColors.enum';
import {
  getMarksStyles,
  getTrackStyles,
  IncomeRation,
  MonthlyPaymentSliderWrapper,
  MonthlyPaymentTooltipText,
  PaymentRation,
  railStyles,
  RatioPercent,
  SliderLabel,
  sliderStyles,
  tipProps,
} from './styles';
import { CreditScore, CreditScoreColor } from '../types';
import { ITier } from '../../Lender/types/Lender.types';
import { TFetchingVehicleFlagsSelector } from '../../core/types';

const { createSliderWithTooltip } = Slider;

const SliderUI = createSliderWithTooltip(Slider);

const POINTER_FAST_STEP = 10;
const POINTER_SLOW_STEP = 1;
const POINTER_MIN_FAST_SPEED = 0.05;

export interface IMonthlyPaymentSlider {
  name: string;
  creditScore: CreditScore | ITier;
  income: number;
  fetchingVehicleFlagsSelector: TFetchingVehicleFlagsSelector;
  disabled?: boolean;
  isRouteOneFlow: boolean;
}

const MonthlyPaymentSlider: FC<IMonthlyPaymentSlider> = ({
  name,
  creditScore,
  income,
  disabled,
  fetchingVehicleFlagsSelector,
  isRouteOneFlow,
}) => {
  const { t } = useTranslation();
  const { setValue, watch, formState } = useFormContext();
  const { resetCount } = useSelector(fetchingVehicleFlagsSelector, shallowEqual);
  const { desiredMonthlyPayment, feesAndTaxes } = watch();
  const { salesTaxRate } = feesAndTaxes;

  const {
    max_allowed_pti,
    max_allowed_payment,
    max_green_pti,
    max_yellow_payment,
    max_green_payment,
    max_yellow_pti,
  } = creditScore;

  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isFirstSet, setIsFirstSet] = useState(true);
  const [maximalPaymentRange, setMaximalPaymentRange] = useState(DEFAULT_MAXIMAL_PAYMENT_RANGE);
  const [sliderColor, setSliderColor] = useState<CreditScoreColor>(CreditScoreColors.GREY);
  const isPointerInteractingRef = useRef(false);
  const pointerDragSampleRef = useRef<any>(null);
  const prevIncomeRef = useRef(income);
  const sliderLabel = t('components.monthlyPaymentSlider.title');

  useEffect(() => {
    const clearPointer = () => {
      isPointerInteractingRef.current = false;
      pointerDragSampleRef.current = null;
    };

    window.addEventListener('pointerup', clearPointer);
    window.addEventListener('pointercancel', clearPointer);

    return () => {
      window.removeEventListener('pointerup', clearPointer);
      window.removeEventListener('pointercancel', clearPointer);
    };
  }, []);

  const incomeRatio = useMemo(
    () =>
      ProbabilityEstimatorCalculationService.getPercentPaymentToIncomeRatio(
        income,
        desiredMonthlyPayment,
        salesTaxRate,
      ),
    [income, desiredMonthlyPayment, salesTaxRate],
  );

  const trimFormatSliderLabel = useCallback(
    (value: number) => (
      <MonthlyPaymentTooltipText color={sliderColor}>{moneyFormatter(value)}</MonthlyPaymentTooltipText>
    ),
    [sliderColor],
  );

  const handleChange = useCallback(
    (value: number) => {
      let next = value;

      if (isPointerInteractingRef.current && value !== maximalPaymentRange) {
        const currentTimestamp = performance.now();
        const lastDragSample = pointerDragSampleRef.current;
        let step = POINTER_SLOW_STEP;

        if (lastDragSample && currentTimestamp > lastDragSample.time) {
          const speed = Math.abs(value - lastDragSample.value) / (currentTimestamp - lastDragSample.time);

          if (speed >= POINTER_MIN_FAST_SPEED) {
            step = POINTER_FAST_STEP;
          }
        }

        pointerDragSampleRef.current = { value, time: currentTimestamp };
        const rounded = Math.round(value / step) * step;

        next = Math.min(maximalPaymentRange, Math.max(MINIMAL_PAYMENT_RANGE, rounded));
      }

      setValue(name, next, { shouldDirty: true, shouldValidate: true });
    },
    [setValue, name, maximalPaymentRange],
  );

  const marks = {
    [MINIMAL_PAYMENT_RANGE]: getMarksStyles(t('components.monthlyPaymentSlider.moreLikely')),
    [maximalPaymentRange]: getMarksStyles(t('components.monthlyPaymentSlider.lessLikely')),
  };

  const scoreId = 'id' in creditScore ? creditScore.id : creditScore.tier_id;

  useEffect(() => {
    setSliderColor(
      ProbabilityEstimatorCalculationService.getMonthlyPaymentColor(
        incomeRatio,
        desiredMonthlyPayment,
        { max_green_payment, max_yellow_payment, max_green_pti, max_yellow_pti } as CreditScore,
        disabled,
      ),
    );
  }, [
    desiredMonthlyPayment,
    disabled,
    incomeRatio,
    max_green_payment,
    max_green_pti,
    max_yellow_payment,
    max_yellow_pti,
  ]);

  useEffect(() => {
    let maximalPayment = 0;

    if (
      typeof income !== 'undefined' &&
      typeof max_allowed_payment !== 'undefined' &&
      typeof max_allowed_pti !== 'undefined'
    ) {
      maximalPayment =
        ProbabilityEstimatorCalculationService.getMaximalMonthlyPayment(max_allowed_pti, max_allowed_payment, income) ||
        DEFAULT_MAXIMAL_PAYMENT_RANGE;

      setMaximalPaymentRange(maximalPayment);
    }

    if (!isFirstRender) {
      const isPaymentDirty = !!formState?.dirtyFields?.[name];
      const isDesiredMonthlyPaymentDefined = typeof desiredMonthlyPayment === 'number' && desiredMonthlyPayment > 0;
      const effectiveMaxPayment = maximalPayment || maximalPaymentRange || DEFAULT_MAXIMAL_PAYMENT_RANGE;
      const clampedPayment =
        effectiveMaxPayment > 0 && desiredMonthlyPayment > effectiveMaxPayment
          ? effectiveMaxPayment
          : desiredMonthlyPayment;
      const isReset = !!resetCount;
      const incomeChanged = prevIncomeRef.current !== income;

      if (!isPaymentDirty || isReset) {
        const keepRouteOnePayment =
          isRouteOneFlow && isDesiredMonthlyPaymentDefined && (isFirstSet || isReset || !incomeChanged);

        if (keepRouteOnePayment) {
          setValue(name, clampedPayment, { shouldDirty: false });

          if (!!scoreId) setIsFirstSet(false);
        } else {
          setValue(
            name,
            ProbabilityEstimatorCalculationService.getDefaultMonthlyPayment(
              max_green_pti,
              income,
              effectiveMaxPayment,
              max_green_payment,
              salesTaxRate,
            ),
            { shouldDirty: false },
          );
        }
      }
    }

    prevIncomeRef.current = income;
    setIsFirstRender(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreId, income, max_green_payment, max_green_pti, name, max_allowed_payment, max_allowed_pti, salesTaxRate]);

  return (
    <MonthlyPaymentSliderWrapper
      disabled={disabled}
      onPointerDownCapture={() => {
        isPointerInteractingRef.current = true;
        pointerDragSampleRef.current = null;
      }}
    >
      <Col xs={ColumnWidth.FULL}>
        <SliderLabel className="payment-range">{sliderLabel}</SliderLabel>
        <SliderUI
          min={MINIMAL_PAYMENT_RANGE}
          max={maximalPaymentRange}
          value={desiredMonthlyPayment}
          onChange={handleChange}
          marks={marks}
          tipProps={tipProps}
          tipFormatter={trimFormatSliderLabel}
          railStyle={railStyles}
          trackStyle={getTrackStyles(sliderColor)}
          handleStyle={sliderStyles}
        />
        <PaymentRation>
          <IncomeRation>{t('components.monthlyPaymentSlider.incomeRatio')}</IncomeRation>
          <RatioPercent color={sliderColor}>{percentFormatter(incomeRatio)}</RatioPercent>
        </PaymentRation>
      </Col>
    </MonthlyPaymentSliderWrapper>
  );
};

export const MemoizedMonthlyPaymentSlider = React.memo(MonthlyPaymentSlider);
